import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { from, Observable, of, Subject, throwError, TimeoutError } from 'rxjs';
import {
  getIdTokenFromFlutterWebViewEvent,
  isInFlutterWrapper,
} from '@security/flutter-authentication.utils';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs/operators';
import { AuthenticationService } from '@security/authentication.service';
import { MsalTokenProviderService } from '@services/msal-token-provider.service';
import {
  INTERNAL_TOKEN_EXPIRATION_LIMIT_MS,
  INTERNAL_TOKEN_LIFETIME_MS,
  INTERNAL_TOKEN_TIMEOUT,
  SESSION_REFRESH_CHANNEL,
  SessionRefreshDataChannel,
  SessionRefreshStatus,
} from '@workers/keep-alive/keep-alive.config';
import { MsalAuthenticationService } from '@security/msal-authentication.service';
import { Router } from '@angular/router';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { ROUTES } from '@shared/config';
import {
  LOGIN_ERROR_QUERY_PARAM_NAME,
  LoginErrorType,
} from 'src/app/should-login/should-login.config';

@Injectable({ providedIn: 'root' })
export class RefreshSessionService implements OnDestroy {
  private readonly authService = inject(AuthenticationService);
  private readonly msalAuthService = inject(MsalAuthenticationService);
  private readonly msalTokenProviderService = inject(MsalTokenProviderService);
  private readonly router = inject(Router);
  private readonly _isInternalTokenRefreshing = signal<boolean>(false);
  private readonly sessionRefreshChannel = new BroadcastChannel(
    SESSION_REFRESH_CHANNEL
  );
  private readonly remoteRefreshFinished$ =
    new Subject<SessionRefreshDataChannel>();
  private internalRefreshInFlight$: Observable<void> | null = null;
  private remoteRefreshStartedAt: number | null = null;
  private internalTokenExpMs = 0;
  public readonly isInternalTokenRefreshing =
    this._isInternalTokenRefreshing.asReadonly();

  constructor() {
    this.sessionRefreshChannel.onmessage = (
      event: MessageEvent<SessionRefreshDataChannel>
    ) => {
      this.handleSessionRefreshMessage(event.data);
    };
  }

  public ngOnDestroy(): void {
    this.sessionRefreshChannel.close();
    this.remoteRefreshFinished$.complete();
  }

  public getInternalTokenExpiration(): number {
    return Math.max(
      this.authService.accessTokenExpirationMs(),
      this.internalTokenExpMs
    );
  }

  public isInternalTokenExpired(): boolean {
    return (
      this.getInternalTokenExpiration() - Date.now() <
      INTERNAL_TOKEN_EXPIRATION_LIMIT_MS
    );
  }

  /**
   * Refreshes the internal OR token.
   * Returns the new expiration timestamp in milliseconds.
   */
  public refreshInternalToken(): Observable<void> {
    if (this.internalRefreshInFlight$) {
      return this.internalRefreshInFlight$;
    }

    const refresh$ = this.isRemoteRefreshInProgress()
      ? this.waitForRemoteRefreshOrTimeout$()
      : this.startInternalRefresh$();

    this.internalRefreshInFlight$ = refresh$.pipe(
      finalize(() => {
        this.internalRefreshInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.internalRefreshInFlight$;
  }

  /**
   * Refreshes the MSAL token and PowerBI tokens.
   * Returns the new expiration timestamp in milliseconds.
   */
  public refreshMsalToken(): Observable<number> {
    const getAccessToken$ = isInFlutterWrapper()
      ? from(getIdTokenFromFlutterWebViewEvent())
      : this.msalAuthService
          .authenticateFromWebBrowser({
            forceRefresh: true,
            allowInteraction: false,
          })
          .pipe(map(auth => auth?.idToken));

    return getAccessToken$.pipe(
      tap(() => this.msalAuthService.restoreSession()),
      map(() => this.getMsalTokenExpiration())
    );
  }

  public refreshMsalAccessTokens(): Observable<void> {
    return this.msalTokenProviderService.refreshExpiredTokens();
  }

  /**
   * Gets the current MSAL token expiration timestamp in milliseconds.
   */
  public getMsalTokenExpiration(): number {
    return this.msalAuthService.getSessionExpiration();
  }

  private startInternalRefresh$(): Observable<void> {
    this._isInternalTokenRefreshing.set(true);
    this.sessionRefreshChannel.postMessage({
      type: SessionRefreshStatus.STARTED,
      timestamp: Date.now(),
    });

    return this.authService.refreshToken().pipe(
      tap(() => {
        const internalTokenExpiration = this.getInternalTokenExpiration();
        this.internalTokenExpMs = internalTokenExpiration;
        this.sessionRefreshChannel.postMessage({
          type: SessionRefreshStatus.COMPLETED,
          timestamp: internalTokenExpiration,
        });
      }),
      catchError(() => {
        this.sessionRefreshChannel.postMessage({
          type: SessionRefreshStatus.FAILED,
          timestamp: Date.now(),
        });
        // Avoid tight retry loops when there is no session/refresh token
        this.internalTokenExpMs = Date.now() + INTERNAL_TOKEN_LIFETIME_MS;
        this.handleRefreshFailure();

        return of(undefined);
      }),
      finalize(() => this._isInternalTokenRefreshing.set(false))
    );
  }

  /**
   * Handle a definitive internal-refresh failure.
   */
  private handleRefreshFailure(): void {
    if (!LocalStorageService.isLoggedIn()) {
      return;
    }

    LocalStorageService.cleanCookies();
    this.router.navigate([ROUTES.shouldLogin], {
      queryParams: {
        [LOGIN_ERROR_QUERY_PARAM_NAME]: LoginErrorType.SESSION_EXPIRED,
      },
    });
  }

  private isRemoteRefreshInProgress(): boolean {
    if (!this.remoteRefreshStartedAt) {
      return false;
    }

    const hasTimedOut =
      Date.now() - this.remoteRefreshStartedAt > INTERNAL_TOKEN_TIMEOUT;
    if (hasTimedOut) {
      this.remoteRefreshStartedAt = null;
      return false;
    }

    return true;
  }

  private waitForRemoteRefreshOrTimeout$(): Observable<void> {
    const timeoutMs = this.getRemoteRefreshRemainingTimeout();

    return this.remoteRefreshFinished$.pipe(
      take(1),
      timeout({ first: timeoutMs }),
      catchError(error => {
        if (error instanceof TimeoutError) {
          return of({
            type: SessionRefreshStatus.FAILED,
            timestamp: Date.now(),
          } as SessionRefreshDataChannel);
        }

        return throwError(() => error);
      }),
      switchMap(event => {
        if (event.type === SessionRefreshStatus.COMPLETED) {
          return of(undefined);
        }

        return this.startInternalRefresh$();
      })
    );
  }

  private getRemoteRefreshRemainingTimeout(): number {
    if (!this.remoteRefreshStartedAt) {
      return INTERNAL_TOKEN_TIMEOUT;
    }

    const elapsedMs = Date.now() - this.remoteRefreshStartedAt;
    return Math.max(0, INTERNAL_TOKEN_TIMEOUT - elapsedMs);
  }

  private handleSessionRefreshMessage(event: SessionRefreshDataChannel): void {
    switch (event.type) {
      case SessionRefreshStatus.STARTED:
        this.remoteRefreshStartedAt = event.timestamp;
        break;
      case SessionRefreshStatus.COMPLETED:
        this.remoteRefreshStartedAt = null;
        this.internalTokenExpMs = event.timestamp;
        this.remoteRefreshFinished$.next(event);
        break;
      case SessionRefreshStatus.FAILED:
        this.remoteRefreshStartedAt = null;
        this.remoteRefreshFinished$.next(event);
        break;
    }
  }
}
